import {formatDate} from '@/dayjs'
import {useAgendaNavigation} from '@/features/agenda/hooks/useAgendaNavigation'
import {useAgendaSearchParams} from '@/features/agenda/hooks/useAgendaSearchParams'
import {httpGetPatient} from '@/features/patients/services/patients.http'
import {PatientSmall} from '@/features/patients/types'
import {httpGetProjectsByPatientDate} from '@/features/projects/http/projects.http'
import {ProjectSmall} from '@/features/projects/types'
import {QUERY_KEYS} from '@/queryClient'
import {raise} from '@/utilities/helpers'
import {useQueries} from '@tanstack/react-query'

export const useAgendaCategoryTeam = (): {
    team: ProjectSmall['team']
    patient: PatientSmall
    projectTeam: ProjectSmall
    isLoading: boolean
    isError: boolean
    isSuccess: boolean
} => {
    const {searchParams} = useAgendaSearchParams()
    const {
        params: {date}
    } = useAgendaNavigation()

    const isEnabledQuery = searchParams.category == 'team' && !!searchParams.patientId && !!date

    const result = useQueries({
        queries: [
            {
                queryKey: [QUERY_KEYS.PROJECTS_BY_PATIENT, searchParams.patientId, date],
                queryFn: () => {
                    return httpGetProjectsByPatientDate({
                        urlParams: {patientId: searchParams.patientId ?? ''},
                        params: {
                            responseType: 'small',
                            date: formatDate(date ?? raise('date is required'), 'YYYY-MM-DD')
                        }
                    })
                },
                enabled: isEnabledQuery
            },
            {
                queryKey: [QUERY_KEYS.PATIENTS, searchParams.patientId, 'small'],
                queryFn: () => {
                    return httpGetPatient({
                        urlParams: {patientId: searchParams.patientId ?? ''},
                        params: {responseType: 'small'}
                    })
                },
                enabled: isEnabledQuery
            }
        ],
        combine: data => {
            return {
                team: data[0].data?.flatMap((project: ProjectSmall) => project.team) || ([] as ProjectSmall['team']),
                projectTeam: data[0].data?.[0],
                patient: data[1]?.data,
                isLoading: data.some(result => result.isLoading),
                isError: data.some(result => result.isError),
                isSuccess: data.every(result => result.isSuccess)
            }
        }
    })

    return result
}
